import React, { useState, useRef, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import AppSelect from "@/components/shared/AppSelect";
import ms from "@features/users/ui/modals/styles/ModalStyles.module.css";

// Columnas editables del lote, en el mismo orden que la tabla de máquinas
// (MaquinasTable.jsx). Fuente única del orden para tabla, refs y navegación.
const BATCH_COLUMNS = [
  { field: "numero", label: "Número", placeholder: "Número", width: 100, propagate: false },
  { field: "tipoespecifico", label: "Tipo Específico", placeholder: "Tipo específico", width: 180, propagate: true },
  { field: "ubicacion", label: "Ubicación", placeholder: "Ubicación", width: 180, propagate: true },
  { field: "ubicacionespecifica", label: "Ubicación Específica", placeholder: "Ubicación específica", width: 180, propagate: true },
  { field: "areaprotegida", label: "Área Protegida", placeholder: "Área protegida", width: 180, propagate: true },
  { field: "modulo", label: "Módulo", placeholder: "Módulo", width: 120, propagate: true },
  { field: "diametro", label: "Diámetro", placeholder: "Diámetro", width: 120, propagate: true },
  { field: "medidanominal", label: "Medida Nominal", placeholder: "Medida nominal", width: 150, propagate: true },
  { field: "descripcion", label: "Descripción", placeholder: "Descripción", width: 200, propagate: true },
];

const TOTAL_COLS = BATCH_COLUMNS.length;

// Definición de todas las columnas visibles del paso 2 (incluye N° y Tipo),
// en orden. Se usa para render, fijado (sticky) y navegación por teclado.
// gridCol = índice de la celda editable de texto para refs/flechas (0..8).
const STEP2_COLUMNS = [
  { key: "num", label: "N°", width: 50, kind: "index" },
  { key: "numero", label: "Número", width: 100, kind: "text", field: "numero", placeholder: "Número", gridCol: 0 },
  { key: "tipo", label: "Tipo", width: 170, kind: "tipo" },
  ...BATCH_COLUMNS.slice(1).map((c, i) => ({
    key: c.field,
    label: c.label,
    width: c.width,
    placeholder: c.placeholder,
    kind: "text",
    field: c.field,
    gridCol: i + 1,
  })),
];

// Ancho total de la tabla (para min-width y scroll horizontal)
const STEP2_TABLE_WIDTH = STEP2_COLUMNS.reduce((sum, c) => sum + c.width, 0);

// Encabezados esperados del archivo, en el orden de la tabla de máquinas.
// "Tipo" es opcional (si falta, se usa el tipo seleccionado en el paso 1).
// La planta NO se toma del archivo: se elige en el paso 1 y aplica a todas.
export const IMPORT_HEADERS = [
  "Número",
  "Tipo",
  "Tipo Específico",
  "Ubicación",
  "Ubicación Específica",
  "Área Protegida",
  "Módulo",
  "Diámetro",
  "Medida Nominal",
  "Descripción",
];

// Fila de máquina vacía con todos los campos editables
const emptyMachineFields = () =>
  BATCH_COLUMNS.reduce((acc, { field }) => ({ ...acc, [field]: "" }), {});

// Normaliza un encabezado (minúsculas, sin acentos ni símbolos) para poder
// emparejar los encabezados del archivo con los campos de la máquina.
const normalizeHeader = (h) =>
  String(h)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

// Encabezado normalizado -> campo. Las etiquetas normalizan a su propio campo
// (p.ej. "Ubicación Específica" -> "ubicacionespecifica"); se añaden alias.
// "Tipo" se resuelve de texto a ID (name -> id) por fila. La planta se toma
// del select del paso 1 (misma para todas), no del archivo.
const FIELD_BY_HEADER = BATCH_COLUMNS.reduce(
  (acc, { field }) => ({ ...acc, [field]: field }),
  {
    no: "numero",
    num: "numero",
    area: "areaprotegida",
    medida: "medidanominal",
    ubicacionesp: "ubicacionespecifica",
    descripciones: "descripcion",
    tipo: "idtipomaquina",
    tipomaquina: "idtipomaquina",
    tipodemaquina: "idtipomaquina",
  },
);

// Normaliza el valor de una celda: recorta espacios y trata "-" como vacío
// (el archivo del usuario usa "-" para indicar celdas sin dato).
const cleanCell = (value) => {
  const s = value == null ? "" : String(value).trim();
  return s === "-" ? "" : s;
};

// Construye un mapa nombre-normalizado -> id a partir de { id: nombre }
export const buildNameToId = (map = {}) =>
  Object.entries(map).reduce((acc, [id, nombre]) => {
    acc[normalizeHeader(nombre)] = String(id);
    return acc;
  }, {});

// Mapea filas crudas (de sheet_to_json) a máquinas editables, emparejando los
// encabezados con los campos. Resuelve "Tipo" de texto a ID por fila, descarta
// filas vacías y reporta los nombres de tipo que no se pudieron resolver.
// Si "Tipo Específico" viene vacío, se autocompleta con el nombre del tipo.
export function mapImportedRows(
  rows,
  { base = {}, tipoByName = {}, tipoNameById = {} } = {},
) {
  let matchedAnyHeader = false;
  const machines = [];
  const unresolved = [];

  rows.forEach((row, index) => {
    const machine = { ...emptyMachineFields(), ...base };
    let hasValue = false;
    let tipoText = "";

    for (const [header, value] of Object.entries(row)) {
      const field = FIELD_BY_HEADER[normalizeHeader(header)];
      if (!field) continue;
      matchedAnyHeader = true;
      const clean = cleanCell(value);
      if (!clean) continue;
      hasValue = true;

      if (field === "idtipomaquina") {
        tipoText = clean;
        const resolvedId = tipoByName[normalizeHeader(clean)];
        if (resolvedId) {
          machine.idtipomaquina = resolvedId;
        } else {
          unresolved.push({ fila: index + 1, columna: "Tipo", valor: clean });
        }
      } else {
        machine[field] = clean;
      }
    }

    if (hasValue) {
      // Autocompletar "Tipo Específico" vacío con el nombre del tipo
      if (!machine.tipoespecifico) {
        machine.tipoespecifico =
          tipoNameById[machine.idtipomaquina] || tipoText || "";
      }
      machines.push(machine);
    }
  });

  return { machines, matchedAnyHeader, unresolved };
}

function BatchMachineModal({ isOpen, onClose, onSave, tipoMaquinas, plantas, saving }) {
  const modalRef = useRef(null);
  const [step, setStep] = useState(1); // Paso 1: Configuración inicial, Paso 2: Detalles

  const [batchConfig, setBatchConfig] = useState({
    idtipomaquina: "",
    idplanta: "",
    cantidad: 1,
    tipoespecifico: "",
  });

  const [machines, setMachines] = useState([]);
  const [validation, setValidation] = useState({
    idtipomaquina: { valid: true, message: "" },
    idplanta: { valid: true, message: "" },
    cantidad: { valid: true, message: "" },
  });

  // Estado para manejar datos guardados
  const [hasSavedData, setHasSavedData] = useState(false);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);

  const [formSubmitted, setFormSubmitted] = useState(false);

  // Referencia para almacenar los inputs de la tabla
  const tableInputsRef = useRef([]);

  // Referencia al input de archivo (Excel/CSV) y estado de importación
  const fileInputRef = useRef(null);
  const [importError, setImportError] = useState("");
  const [importInfo, setImportInfo] = useState("");

  // Guard de reentrada para evitar doble envío del lote (doble clic)
  const submittingRef = useRef(false);

  // Columnas fijadas (sticky) en el paso 2. Por defecto: N° y Número.
  const [pinnedColumns, setPinnedColumns] = useState(
    () => new Set(["num", "numero"]),
  );

  const togglePinnedColumn = (key) => {
    setPinnedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Metadatos de columnas del paso 2 con offset izquierdo para las fijadas
  const step2ColumnsMeta = useMemo(() => {
    let leftAcc = 0;
    return STEP2_COLUMNS.map((c) => {
      const pinned = pinnedColumns.has(c.key);
      const meta = { ...c, pinned, left: pinned ? leftAcc : null };
      if (pinned) leftAcc += c.width;
      return meta;
    });
  }, [pinnedColumns]);

  // Clave para localStorage
  const STORAGE_KEY = "batchMachineFormData";

  // Comprobar si hay datos guardados al abrir el modal
  useEffect(() => {
    if (isOpen) {
      const savedData = getSavedFormData();
      if (savedData) {
        setHasSavedData(true);
        setShowRestorePrompt(true);
      } else {
        resetFormToDefault();
      }
    }
  }, [isOpen]);

  // Guardar datos al cerrar el modal si hay cambios
  const handleCloseWithAutosave = () => {
    // No permitir cerrar mientras se está guardando el lote
    if (saving || submittingRef.current) return;
    if (
      batchConfig.idtipomaquina ||
      batchConfig.idplanta ||
      batchConfig.tipoespecifico ||
      batchConfig.cantidad !== 1 ||
      (machines.length > 0 &&
        machines.some(
          (m) =>
            m.numero ||
            m.ubicacion ||
            m.ubicacionespecifica ||
            m.tipoespecifico ||
            m.areaprotegida ||
            m.modulo ||
            m.diametro ||
            m.medidanominal ||
            m.descripcion,
        ))
    ) {
      // Hay datos para guardar
      saveFormData({
        step,
        batchConfig,
        machines,
        timestamp: new Date().toISOString(),
      });
    }

    onClose();
  };

  // Guardar datos del formulario en localStorage
  const saveFormData = (data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Error saving form data:", error);
    }
  };

  // Obtener datos guardados
  const getSavedFormData = () => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error retrieving saved form data:", error);
      return null;
    }
  };

  // Limpiar los datos guardados
  const clearSavedFormData = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setHasSavedData(false);
    } catch (error) {
      console.error("Error clearing saved form data:", error);
    }
  };

  // Restaurar datos guardados
  const restoreSavedData = () => {
    const savedData = getSavedFormData();
    if (savedData) {
      setBatchConfig(
        savedData.batchConfig || {
          idtipomaquina: "",
          idplanta: "",
          cantidad: 1,
          tipoespecifico: "",
        },
      );
      setMachines(savedData.machines || []);
      setStep(savedData.step || 1);
      setShowRestorePrompt(false);

      // Si hay máquinas guardadas, preparar las referencias
      if (savedData.machines && savedData.machines.length > 0) {
        tableInputsRef.current = Array(savedData.machines.length)
          .fill()
          .map(() => Array(TOTAL_COLS).fill(null));
      }
    }
  };

  // Descartar datos guardados
  const discardSavedData = () => {
    clearSavedFormData();
    resetFormToDefault();
    setShowRestorePrompt(false);
  };

  // Resetear a valores predeterminados
  const resetFormToDefault = () => {
    setBatchConfig({
      idtipomaquina: "",
      idplanta: "",
      cantidad: 1,
      tipoespecifico: "",
    });
    setMachines([]);
    setStep(1);
    setFormSubmitted(false);
    setImportError("");
    setImportInfo("");
    setValidation({
      idtipomaquina: { valid: true, message: "" },
      idplanta: { valid: true, message: "" },
      cantidad: { valid: true, message: "" },
    });
    // Reset tableInputsRef cuando se abre el modal
    tableInputsRef.current = [];
  };

  // Manejador de Escape para cerrar el modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleCloseWithAutosave();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, batchConfig, machines, step]);

  // Focus en el primer campo al abrir el modal
  useEffect(() => {
    if (isOpen && modalRef.current && !showRestorePrompt) {
      const firstInput = modalRef.current.querySelector("select, input, textarea");
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100);
      }
    }
  }, [isOpen, showRestorePrompt]);

  // Dimensionar todos los textarea al entrar/cambiar el listado del paso 2
  useEffect(() => {
    if (step !== 2) return;
    requestAnimationFrame(() => {
      tableInputsRef.current.forEach((row) =>
        row?.forEach((el) => autoGrow(el)),
      );
    });
  }, [step, machines.length]);

  const plantaOptions = useMemo(
    () => [
      { value: "", label: "Seleccione una planta" },
      ...Object.entries(plantas).map(([id, nombre]) => ({
        value: id,
        label: nombre,
      })),
    ],
    [plantas],
  );

  const tipoMaquinaOptions = useMemo(
    () => [
      { value: "", label: "Seleccione un tipo" },
      ...Object.entries(tipoMaquinas).map(([id, nombre]) => ({
        value: id,
        label: nombre,
      })),
    ],
    [tipoMaquinas],
  );

  // Mapa nombre-normalizado -> id para resolver "Tipo" al importar
  const tipoByName = useMemo(() => buildNameToId(tipoMaquinas), [tipoMaquinas]);

  // Función para validar campos
  const validateField = (name, value) => {
    switch (name) {
      case "idtipomaquina":
        return {
          valid: !!value,
          message: !value ? "Debe seleccionar un tipo de máquina" : "",
        };
      case "idplanta":
        return {
          valid: !!value,
          message: !value ? "Debe seleccionar una planta" : "",
        };
      case "cantidad":
        // Permitir campo vacío durante la edición, pero no al validar
        if (value === "" || value === null || value === undefined) {
          return {
            valid: false,
            message: "La cantidad es obligatoria",
          };
        }
        return {
          valid: value > 0 && value <= 50,
          message:
            value <= 0
              ? "La cantidad debe ser mayor que 0"
              : value > 50
                ? "Máximo 50 máquinas por lote"
                : "",
        };
      default:
        return { valid: true, message: "" };
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Modificación aquí para permitir valores vacíos en el campo cantidad
    if (name === "cantidad") {
      // Si el valor está vacío, guardar una cadena vacía temporalmente
      // para permitir que el usuario borre y escriba un nuevo valor
      const newValue = value === "" ? "" : parseInt(value, 10);

      setBatchConfig((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    } else {
      setBatchConfig((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (formSubmitted) {
      setValidation((prev) => ({
        ...prev,
        [name]: validateField(
          name,
          name === "cantidad" && value === ""
            ? ""
            : name === "cantidad"
              ? parseInt(value, 10)
              : value,
        ),
      }));
    }
  };

  // Genera las filas de máquinas basadas en la cantidad
  const generateMachineRows = () => {
    // Validar campos
    const tipoValidation = validateField(
      "idtipomaquina",
      batchConfig.idtipomaquina,
    );
    const plantaValidation = validateField("idplanta", batchConfig.idplanta);

    // Si el campo de cantidad está vacío, usar el valor predeterminado 1 para la validación
    const cantidadValue =
      batchConfig.cantidad === "" ? 1 : batchConfig.cantidad;
    const cantidadValidation = validateField("cantidad", cantidadValue);

    setValidation({
      idtipomaquina: tipoValidation,
      idplanta: plantaValidation,
      cantidad: cantidadValidation,
    });
    setFormSubmitted(true);

    // Verificar si hay errores
    if (
      !tipoValidation.valid ||
      !plantaValidation.valid ||
      !cantidadValidation.valid
    ) {
      return;
    }

    // Si llegamos aquí y cantidad está vacía, usamos 1 como valor predeterminado
    const finalCantidad =
      batchConfig.cantidad === "" ? 1 : batchConfig.cantidad;

    // Actualizar el estado con el valor final
    if (batchConfig.cantidad === "") {
      setBatchConfig((prev) => ({
        ...prev,
        cantidad: 1,
      }));
    }

    // Crear un array de máquinas basado en la cantidad
    const newMachines = Array.from({ length: finalCantidad }, () => ({
      ...emptyMachineFields(),
      idtipomaquina: batchConfig.idtipomaquina,
      idplanta: batchConfig.idplanta,
      tipoespecifico: batchConfig.tipoespecifico || "",
      estado: true,
    }));

    setMachines(newMachines);
    setImportInfo("");
    setStep(2); // Avanzar al paso 2

    // Inicializar la matriz de referencias para los inputs
    tableInputsRef.current = Array(finalCantidad)
      .fill()
      .map(() => Array(TOTAL_COLS).fill(null));
  };

  // Actualiza un campo específico para una máquina específica
  const handleMachineFieldChange = (index, field, value) => {
    const updatedMachines = [...machines];
    updatedMachines[index][field] = value;
    setMachines(updatedMachines);
  };

  // Prevenir envío de formulario al presionar Enter
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Evitar envío del formulario con Enter
    }
  };

  // Nuevo manejador para navegación con flechas en la tabla
  const handleTableKeyDown = (e, rowIndex, colIndex) => {
    // Evitar el envío del formulario al presionar Enter
    if (e.key === "Enter") {
      e.preventDefault();
      return;
    }

    const inputs = tableInputsRef.current;
    const totalRows = machines.length;
    const totalCols = TOTAL_COLS; // Columnas editables (sin contar el número de fila)

    let nextRow = rowIndex;
    let nextCol = colIndex;

    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        nextRow = Math.max(0, rowIndex - 1);
        break;
      case "ArrowDown":
        e.preventDefault();
        nextRow = Math.min(totalRows - 1, rowIndex + 1);
        break;
      case "ArrowLeft":
        if (e.target.selectionStart === 0) {
          // Solo si el cursor está al inicio
          e.preventDefault();
          nextCol = Math.max(0, colIndex - 1);
        }
        break;
      case "ArrowRight":
        if (e.target.selectionStart === e.target.value.length) {
          // Solo si el cursor está al final
          e.preventDefault();
          nextCol = Math.min(totalCols - 1, colIndex + 1);
        }
        break;
      default:
        return; // Para otras teclas, no hacer nada
    }

    // Si la posición ha cambiado, enfocar el nuevo input
    if (nextRow !== rowIndex || nextCol !== colIndex) {
      const nextInput = inputs[nextRow][nextCol];
      if (nextInput) {
        nextInput.focus();
        // Colocar el cursor al final del texto
        setTimeout(() => {
          nextInput.selectionStart = nextInput.value.length;
          nextInput.selectionEnd = nextInput.value.length;
        }, 0);
      }
    }
  };

  // Guardar referencia al input en la matriz de referencias
  // Ajusta el alto del textarea a su contenido (multi-renglón sin recorte)
  const autoGrow = (el) => {
    if (!el || el.tagName !== "TEXTAREA") return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const setInputRef = (rowIndex, colIndex, element) => {
    if (!tableInputsRef.current[rowIndex]) {
      tableInputsRef.current[rowIndex] = [];
    }
    tableInputsRef.current[rowIndex][colIndex] = element;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Si estamos en el paso 1, generar las filas de máquinas
    if (step === 1) {
      generateMachineRows();
      return;
    }

    // Si estamos en el paso 2, enviar el lote de máquinas
    // Validar que cada máquina tenga tipo (la planta viene del paso 1)
    const sinTipo = machines.reduce(
      (acc, m, i) => (m.idtipomaquina ? acc : [...acc, i + 1]),
      [],
    );
    if (sinTipo.length > 0) {
      setImportError(
        `Falta el Tipo en la(s) fila(s): ${sinTipo.slice(0, 10).join(", ")}${sinTipo.length > 10 ? "…" : ""}. Selecciónelo antes de guardar.`,
      );
      return;
    }

    // Guard de reentrada: evita que un segundo clic dispare otro guardado
    // (el prop `saving` del padre se propaga de forma asíncrona)
    if (submittingRef.current || saving) return;
    submittingRef.current = true;

    try {
      const result = await onSave(machines);
      // Si se guardó correctamente, limpiar el borrador y cerrar el modal
      if (result) {
        clearSavedFormData();
        onClose();
      }
    } finally {
      submittingRef.current = false;
    }
  };

  // Propagar valores de la primera fila a las demás
  const propagateValue = (field, value) => {
    const updatedMachines = machines.map((machine) => ({
      ...machine,
      [field]: value,
    }));
    setMachines(updatedMachines);
    requestAnimationFrame(() => {
      tableInputsRef.current.forEach((row) =>
        row?.forEach((el) => autoGrow(el)),
      );
    });
  };

  // Abre el selector de archivo (Excel/CSV). La planta se elige en el paso 1 y
  // aplica a todas; el tipo puede venir en el archivo (columna "Tipo").
  const handleImportClick = () => {
    const plantaValidation = validateField("idplanta", batchConfig.idplanta);
    setValidation((prev) => ({ ...prev, idplanta: plantaValidation }));
    setFormSubmitted(true);
    setImportError("");
    setImportInfo("");

    if (!plantaValidation.valid) {
      setImportError("Seleccione la planta antes de importar.");
      return;
    }
    fileInputRef.current?.click();
  };

  // Lee el archivo Excel/CSV, mapea encabezados a campos y prellena la tabla
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permitir volver a seleccionar el mismo archivo
    if (!file) return;

    setImportError("");
    setImportInfo("");

    try {
      const data = new Uint8Array(await file.arrayBuffer());
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!sheet) {
        setImportError("El archivo no contiene hojas legibles.");
        return;
      }

      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
      if (rows.length === 0) {
        setImportError("El archivo no contiene filas de datos.");
        return;
      }

      const { machines: imported, matchedAnyHeader, unresolved } =
        mapImportedRows(rows, {
          base: {
            idtipomaquina: batchConfig.idtipomaquina,
            idplanta: batchConfig.idplanta,
            tipoespecifico: batchConfig.tipoespecifico || "",
            estado: true,
          },
          tipoByName,
          tipoNameById: tipoMaquinas,
        });

      if (!matchedAnyHeader) {
        setImportError(
          "No se reconoció ninguna columna. Use los encabezados de la plantilla.",
        );
        return;
      }
      if (imported.length === 0) {
        setImportError("No se encontraron filas con datos para importar.");
        return;
      }

      setMachines(imported);
      tableInputsRef.current = Array(imported.length)
        .fill()
        .map(() => Array(TOTAL_COLS).fill(null));

      const tiposDistintos = new Set(
        imported.map((m) => m.idtipomaquina).filter(Boolean),
      ).size;
      setImportInfo(
        `${imported.length} equipo(s) importado(s)` +
          (tiposDistintos > 1 ? ` · ${tiposDistintos} tipos distintos` : "") +
          ". Revise y edite antes de guardar.",
      );

      if (unresolved.length > 0) {
        const nombres = [...new Set(unresolved.map((u) => u.valor))];
        const detalle = nombres.slice(0, 5).map((n) => `"${n}"`).join(", ");
        setImportError(
          `No se reconoció el Tipo en ${unresolved.length} fila(s): ${detalle}${nombres.length > 5 ? "…" : ""}. Selecciónelo en la columna Tipo antes de guardar.`,
        );
      }

      setStep(2);
    } catch (error) {
      console.error("Error al importar el archivo:", error);
      setImportError(
        "No se pudo leer el archivo. Verifique que sea un Excel (.xlsx) o CSV válido.",
      );
    }
  };

  // Plantilla preparada: conserva la tabla nativa y el formato Texto de Excel.
  const handleDownloadTemplate = async () => {
    setImportError("");
    let url;
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}templates/plantilla_equipos.xlsx`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      // Una ruta inexistente puede devolver el HTML de la SPA con estado 200.
      const signature = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
      if (signature.join() !== "80,75,3,4") throw new Error("Plantilla Excel inválida");
      url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "plantilla_equipos.xlsx";
      link.click();
    } catch (error) {
      console.error("Error al descargar la plantilla:", error);
      setImportError("No se pudo descargar la plantilla Excel. Intente descargarla de nuevo.");
    } finally {
      if (url) setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  // Tiempo transcurrido desde la última modificación (para mostrar al usuario)
  const getTimeElapsed = () => {
    const savedData = getSavedFormData();
    if (!savedData || !savedData.timestamp) return "";

    const saved = new Date(savedData.timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - saved) / (1000 * 60));

    if (diffMinutes < 1) return "hace menos de un minuto";
    if (diffMinutes === 1) return "hace 1 minuto";
    if (diffMinutes < 60) return `hace ${diffMinutes} minutos`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours === 1) return "hace 1 hora";
    if (diffHours < 24) return `hace ${diffHours} horas`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "hace 1 día";
    return `hace ${diffDays} días`;
  };

  if (!isOpen) return null;

  return (
    <div className={ms["modal-overlay"]} onClick={handleCloseWithAutosave}>
      <div
        className={`${ms["modal-content"]} ${step === 2 ? ms["modal-content-large"] : ""}`}
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        {showRestorePrompt ? (
          <div className={ms["restore-prompt"]}>
            <div className={ms["restore-prompt-content"]}>
              <h3>Datos no guardados detectados</h3>
              <p>
                Se encontraron datos sin guardar de una sesión anterior{" "}
                {getTimeElapsed()}.
              </p>
              <p>¿Qué desea hacer?</p>
              <div className={ms["restore-actions"]}>
                <button
                  onClick={restoreSavedData}
                  className={`${ms["modal-button"]} ${ms.save}`}
                >
                  Restaurar datos
                </button>
                <button
                  onClick={discardSavedData}
                  className={`${ms["modal-button"]} ${ms.cancel}`}
                >
                  Descartar y empezar nuevo
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={ms["modal-header"]}>
              <h2>
                {step === 1
                  ? "Añadir Equipos por Lote - Configuración"
                  : `Añadir Equipos por Lote - Detalles (${machines.length})`}
              </h2>
              {hasSavedData && (
                <div className={ms["autosave-indicator"]}>
                  <i className="fa-solid fa-save"></i> Autoguardado activado
                </div>
              )}
              <button
                className={ms["modal-close"]}
                onClick={handleCloseWithAutosave}
                aria-label="Cerrar"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} noValidate>
              <div className={ms["modal-body"]}>
                {/* Paso 1: Configuración inicial */}
                {step === 1 && (
                  <>
                    <div className={ms["form-row"]}>
                      <div className={ms["form-group"]}>
                        <label
                          htmlFor="idplanta"
                          className={ms["required-label"]}
                        >
                          Planta
                        </label>
                        <AppSelect
                          id="idplanta"
                          value={batchConfig.idplanta}
                          onChange={(val) => {
                            setBatchConfig((prev) => ({
                              ...prev,
                              idplanta: val,
                            }));
                            if (formSubmitted) {
                              setValidation((prev) => ({
                                ...prev,
                                idplanta: validateField("idplanta", val),
                              }));
                            }
                          }}
                          options={plantaOptions}
                          placeholder="Seleccione una planta"
                          variant="modal"
                          menuPortalTarget={document.body}
                        />
                        {!validation.idplanta.valid && (
                          <span
                            className={`${ms["validation-message"]} ${ms.show}`}
                          >
                            {validation.idplanta.message}
                          </span>
                        )}
                      </div>

                      <div className={ms["form-group"]}>
                        <label
                          htmlFor="idtipomaquina"
                          className={ms["required-label"]}
                        >
                          Tipo de Máquina
                        </label>
                        <AppSelect
                          id="idtipomaquina"
                          value={batchConfig.idtipomaquina}
                          onChange={(val) => {
                            setBatchConfig((prev) => ({
                              ...prev,
                              idtipomaquina: val,
                            }));
                            if (formSubmitted) {
                              setValidation((prev) => ({
                                ...prev,
                                idtipomaquina: validateField(
                                  "idtipomaquina",
                                  val,
                                ),
                              }));
                            }
                          }}
                          options={tipoMaquinaOptions}
                          placeholder="Seleccione un tipo"
                          variant="modal"
                          menuPortalTarget={document.body}
                        />
                        {!validation.idtipomaquina.valid && (
                          <span
                            className={`${ms["validation-message"]} ${ms.show}`}
                          >
                            {validation.idtipomaquina.message}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Campo para tipo específico común */}
                    <div className={ms["form-group"]}>
                      <label htmlFor="tipoespecifico">
                        Tipo Específico (común para todas)
                      </label>
                      <input
                        id="tipoespecifico"
                        name="tipoespecifico"
                        type="text"
                        value={batchConfig.tipoespecifico}
                        onChange={handleInputChange}
                        placeholder="Dejar en blanco para personalizar individualmente"
                      />
                      <span className={ms["form-text"]}>
                        Este valor se aplicará a todas las máquinas. Si lo deja
                        en blanco, podrá definirlo individualmente para cada
                        máquina.
                      </span>
                    </div>

                    <div className={ms["form-group"]}>
                      <label
                        htmlFor="cantidad"
                        className={ms["required-label"]}
                      >
                        Cantidad de Máquinas (máximo 50)
                      </label>
                      <input
                        id="cantidad"
                        name="cantidad"
                        type="number"
                        min="1"
                        max="50"
                        value={batchConfig.cantidad}
                        onChange={handleInputChange}
                        className={
                          formSubmitted
                            ? validation.cantidad.valid
                              ? ms["input-valid"]
                              : ms["input-invalid"]
                            : ""
                        }
                        aria-required="true"
                      />
                      {!validation.cantidad.valid && (
                        <span
                          className={`${ms["validation-message"]} ${ms.show}`}
                        >
                          {validation.cantidad.message}
                        </span>
                      )}
                    </div>

                    {/* Importar desde Excel/CSV */}
                    <div className={ms["batch-import-section"]}>
                      <div className={ms["batch-import-divider"]}>
                        <span>o importe desde un archivo</span>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                      <div className={ms["batch-import-actions"]}>
                        <button
                          type="button"
                          className={`${ms["modal-button"]} ${ms.secondary}`}
                          onClick={handleImportClick}
                        >
                          <i className="fa-solid fa-file-import" style={{ marginRight: 6 }} />
                          Importar Excel / CSV
                        </button>
                        <button
                          type="button"
                          className={ms["propagate-button"]}
                          onClick={handleDownloadTemplate}
                        >
                          <i className="fa-solid fa-download" style={{ marginRight: 6 }} />
                          Descargar plantilla Excel
                        </button>
                      </div>
                      <span className={ms["form-text"]}>
                        Seleccione primero la <strong>planta</strong> (aplica a
                        todos los equipos). El archivo debe tener encabezados:{" "}
                        {IMPORT_HEADERS.join(", ")}. La columna{" "}
                        <strong>Tipo</strong> puede traer varios tipos por
                        nombre (p. ej. "Detector de Humo"); si se omite, se usa
                        el tipo seleccionado arriba. Se respeta el orden de las
                        columnas y podrá editar todo antes de guardar.
                      </span>
                      {importError && (
                        <span
                          className={`${ms["validation-message"]} ${ms.show}`}
                        >
                          {importError}
                        </span>
                      )}
                    </div>
                  </>
                )}

                {/* Paso 2: Tabla de detalles de máquinas */}
                {step === 2 && (
                  <div className={ms["batch-machines-table-container"]}>
                    <div className={ms["batch-propagate-controls"]}>
                      <div className={ms["form-group"]}>
                        <label>Propagar valores de la primera fila:</label>
                        <div className={ms["propagate-buttons"]}>
                          {BATCH_COLUMNS.filter((c) => c.propagate).map(
                            ({ field, label }) => (
                              <button
                                key={field}
                                type="button"
                                className={ms["propagate-button"]}
                                onClick={() =>
                                  propagateValue(field, machines[0][field])
                                }
                              >
                                {label}
                              </button>
                            ),
                          )}
                        </div>
                      </div>
                      {importInfo && (
                        <div className={ms["batch-import-info"]}>
                          <i className="fa-solid fa-circle-info" style={{ marginRight: 6 }} />
                          {importInfo}
                        </div>
                      )}
                      {importError && (
                        <div className={`${ms["batch-import-info"]} ${ms["batch-import-error"]}`}>
                          <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
                          {importError}
                        </div>
                      )}
                      <div className={ms["keyboard-navigation-help"]}>
                        <p>
                          <i className="fa-solid fa-keyboard"></i> Navegación:
                          Use Tab o flechas para moverse entre celdas
                        </p>
                      </div>
                    </div>
                    <div className={ms["responsive-table-wrapper"]}>
                      <table
                        className={ms["batch-machines-table"]}
                        style={{ minWidth: STEP2_TABLE_WIDTH }}
                      >
                        <thead>
                          <tr>
                            {step2ColumnsMeta.map((c) => (
                              <th
                                key={c.key}
                                className={
                                  c.pinned
                                    ? `${ms["pinned-col"]} ${ms["pinned-th"]}`
                                    : ""
                                }
                                style={{
                                  width: c.width,
                                  left: c.pinned ? c.left : undefined,
                                }}
                              >
                                <div className={ms["th-content"]}>
                                  <span>{c.label}</span>
                                  <button
                                    type="button"
                                    className={`${ms["pin-button"]} ${c.pinned ? ms["pin-active"] : ""}`}
                                    onClick={() => togglePinnedColumn(c.key)}
                                    title={
                                      c.pinned
                                        ? "Desfijar columna"
                                        : "Fijar columna"
                                    }
                                    aria-label={
                                      c.pinned
                                        ? "Desfijar columna"
                                        : "Fijar columna"
                                    }
                                    aria-pressed={c.pinned}
                                  >
                                    <i className="fa-solid fa-thumbtack" />
                                  </button>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {machines.map((machine, rowIndex) => (
                            <tr key={rowIndex} className="table-row">
                              {step2ColumnsMeta.map((c) => {
                                const cellStyle = {
                                  width: c.width,
                                  left: c.pinned ? c.left : undefined,
                                };
                                const cellClass = c.pinned
                                  ? ms["pinned-col"]
                                  : "";

                                if (c.kind === "index") {
                                  return (
                                    <td
                                      key={c.key}
                                      className={`${ms["col-num"]} ${cellClass}`}
                                      style={cellStyle}
                                    >
                                      {rowIndex + 1}
                                    </td>
                                  );
                                }

                                if (c.kind === "tipo") {
                                  return (
                                    <td
                                      key={c.key}
                                      className={cellClass}
                                      style={cellStyle}
                                    >
                                      <select
                                        value={machine.idtipomaquina || ""}
                                        onChange={(e) =>
                                          handleMachineFieldChange(
                                            rowIndex,
                                            "idtipomaquina",
                                            e.target.value,
                                          )
                                        }
                                        className={
                                          machine.idtipomaquina
                                            ? ""
                                            : ms["input-invalid"]
                                        }
                                      >
                                        {tipoMaquinaOptions.map((o) => (
                                          <option key={o.value} value={o.value}>
                                            {o.label}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  );
                                }

                                return (
                                  <td
                                    key={c.key}
                                    className={cellClass}
                                    style={cellStyle}
                                  >
                                    <textarea
                                      rows={1}
                                      value={machine[c.field] || ""}
                                      onChange={(e) => {
                                        handleMachineFieldChange(
                                          rowIndex,
                                          c.field,
                                          e.target.value,
                                        );
                                        autoGrow(e.target);
                                      }}
                                      onKeyDown={(e) =>
                                        handleTableKeyDown(e, rowIndex, c.gridCol)
                                      }
                                      placeholder={c.placeholder}
                                      ref={(el) =>
                                        setInputRef(rowIndex, c.gridCol, el)
                                      }
                                      tabIndex={
                                        rowIndex * TOTAL_COLS + c.gridCol + 1
                                      }
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className={ms["modal-footer"]}>
                {step === 2 && (
                  <button
                    type="button"
                    className={`${ms["modal-button"]} ${ms.secondary}`}
                    onClick={() => setStep(1)}
                    disabled={saving}
                  >
                    Atrás
                  </button>
                )}
                <button
                  type="button"
                  className={`${ms["modal-button"]} ${ms.cancel}`}
                  onClick={handleCloseWithAutosave}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={`${ms["modal-button"]} ${ms.save}`}
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Guardando...</>
                  ) : (
                    step === 1 ? "Siguiente" : "Guardar Lote"
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default BatchMachineModal;
